/**
 * «گزارش‌ها» — the vocabulary, the structure and the derivations both surfaces share.
 *
 * Design: docs/CLINIC_REPORTS_REDESIGN.md. The desktop workbench and the mobile
 * reduction render nothing alike, and that is the doctrine (clinic-app/CLAUDE.md
 * §4.1). What they must never do is disagree about which reports exist, who may see
 * one, what question it answers, or which filters it takes — so all of that lives
 * here and is imported by both.
 *
 * Nothing in this file renders. No React, no JSX, no tokens.
 */
import { costCenterOptions } from '../../lib/costCenter';

import { bucketFor, foldBuckets, PRESET_IDS, presetRange, resolveRange } from '../../lib/dateRange';

export { bucketFor, foldBuckets, PRESET_IDS, presetRange, resolveRange };

// ── The rail ─────────────────────────────────────────────────────────────────
//
// Eight groups, ordered by how often a clinic opens them rather than by how the data
// is stored: مالی first because «آیا سود کرده‌ام؟» is the question that brings
// someone to this screen, انبار and تجهیزات last because they are read weekly.
//
// `perm` is a single key or an array meaning ALL of them — the same contract the BFF
// enforces, restated here so the rail hides exactly what the server would refuse.
// Gating in one shared place (rather than in each surface) is what keeps the desktop
// rail and the mobile section list from drifting on who sees what.

export const RAIL_GROUPS = (t) => ([
  {
    key: 'finance',
    labelFa: t('reports.rail.groups.finance'),
    items: [
      { key: 'pnl', perm: 'reports.finance.view' },
      { key: 'revenue', perm: 'reports.finance.view' },
      { key: 'collections', perm: 'reports.finance.view' },
      { key: 'expenses', perm: 'reports.finance.view' },
      { key: 'receivables', perm: 'reports.finance.view' },
      { key: 'shifts', perm: ['reports.finance.view', 'cashier.view'] },
      { key: 'bills', perm: 'reports.finance.view' },
    ],
  },
  {
    key: 'practitioners',
    labelFa: t('reports.rail.groups.practitioners'),
    items: [
      { key: 'practitioners', perm: 'reports.view' },
      { key: 'practitioner-finance', perm: ['reports.finance.view', 'doctors.view'] },
    ],
  },
  {
    key: 'patients',
    labelFa: t('reports.rail.groups.patients'),
    items: [
      { key: 'patients', perm: 'reports.view' },
      { key: 'patient-value', perm: 'reports.finance.view' },
      { key: 'followups', perm: ['reports.view', 'patients.view'] },
    ],
  },
  {
    key: 'services',
    labelFa: t('reports.rail.groups.services'),
    items: [{ key: 'services', perm: 'reports.view' }],
  },
  {
    key: 'appointments',
    labelFa: t('reports.rail.groups.appointments'),
    items: [
      { key: 'appointments', perm: 'reports.view' },
      { key: 'peak-times', perm: 'reports.view' },
      { key: 'capacity', perm: 'reports.view' },
    ],
  },
  {
    key: 'insurance',
    labelFa: t('reports.rail.groups.insurance'),
    items: [{ key: 'insurance', perm: 'reports.finance.view' }],
  },
  {
    key: 'inventory',
    labelFa: t('reports.rail.groups.inventory'),
    items: [
      { key: 'inventory-value', perm: ['reports.view', 'inventory.view'] },
      { key: 'consumption', perm: ['reports.view', 'inventory.view'] },
      { key: 'stock-movements', perm: ['reports.view', 'inventory.view'] },
    ],
  },
  {
    key: 'equipment',
    labelFa: t('reports.rail.groups.equipment'),
    items: [{ key: 'equipment', perm: ['reports.view', 'equipment.view'] }],
  },
]);

/**
 * The overview is NOT in the rail.
 *
 * It is the module's landing page and its answer — a rail entry beside seven others
 * would make «آیا سود کرده‌ام؟» look like one report among many rather than the
 * reason the screen exists. It is reached by opening /reports, and by the crumb at
 * the top of every report.
 */
export const OVERVIEW = 'overview';

const ALL_VIEWS = RAIL_GROUPS((k) => k).flatMap((g) => g.items);
export const VIEW_KEYS = [OVERVIEW, ...ALL_VIEWS.map((v) => v.key)];

/** The permission(s) a view needs, or null for the overview. */
export const permFor = (viewKey) => ALL_VIEWS.find((v) => v.key === viewKey)?.perm ?? null;

const satisfies = (can, perm) =>
  (Array.isArray(perm) ? perm.every((p) => can(p)) : can(perm));

/**
 * The rail as THIS person may see it.
 *
 * A group with no visible item disappears entirely rather than rendering an empty
 * heading: a منشی holding only `reports.view` sees four groups, not eight with four
 * of them blank, and the module honestly degrades to what they can actually read.
 */
export function visibleRail(can, t) {
  return RAIL_GROUPS(t)
    .map((g) => ({ ...g, items: g.items.filter((i) => satisfies(can, i.perm)) }))
    .filter((g) => g.items.length);
}

/**
 * Which view to open.
 *
 * A forbidden or unknown `?view=` falls back to the OVERVIEW rather than erroring:
 * a stale bookmark or a link from a colleague with different permissions is a
 * normal event, not a fault, and the overview is readable by anyone who can reach
 * this screen at all.
 */
export function resolveView(requested, can) {
  if (!requested || requested === OVERVIEW) return OVERVIEW;
  const perm = permFor(requested);
  if (perm && satisfies(can, perm)) return requested;
  return OVERVIEW;
}

// ── What each report is ──────────────────────────────────────────────────────
//
// `question` is not decoration. It is printed under the title on every report, and
// it is the test a report has to pass to exist at all: if the question cannot be
// written as one sentence a clinic owner would actually ask, the report should not
// be built (design §2 rule 1).
//
// `range: false` marks a POINT-IN-TIME report. Those have no window and no
// comparison — a receivable is what is owed now, and «مطالبات در مقایسه با ماه قبل»
// is a question about a different thing.

export const VIEW_META = (t) => ({
  overview: { title: t('reports.views.overview.title'), question: t('reports.views.overview.question'), range: true, compare: true },
  pnl: { title: t('reports.views.pnl.title'), question: t('reports.views.pnl.question'), range: true, compare: true },
  revenue: { title: t('reports.views.revenue.title'), question: t('reports.views.revenue.question'), range: true, compare: true },
  collections: { title: t('reports.views.collections.title'), question: t('reports.views.collections.question'), range: true, compare: true },
  expenses: { title: t('reports.views.expenses.title'), question: t('reports.views.expenses.question'), range: true, compare: true },
  receivables: { title: t('reports.views.receivables.title'), question: t('reports.views.receivables.question'), range: false, compare: false },
  shifts: { title: t('reports.views.shifts.title'), question: t('reports.views.shifts.question'), range: true, compare: false, defaultRange: 'week' },
  bills: { title: t('reports.views.bills.title'), question: t('reports.views.bills.question'), range: true, compare: false, defaultRange: 'year' },
  practitioners: { title: t('reports.views.practitioners.title'), question: t('reports.views.practitioners.question'), range: true, compare: true },
  'practitioner-finance': { title: t('reports.views.practitionerFinance.title'), question: t('reports.views.practitionerFinance.question'), range: true, compare: true },
  patients: { title: t('reports.views.patients.title'), question: t('reports.views.patients.question'), range: true, compare: true },
  'patient-value': { title: t('reports.views.patientValue.title'), question: t('reports.views.patientValue.question'), range: true, compare: false },
  followups: { title: t('reports.views.followups.title'), question: t('reports.views.followups.question'), range: true, compare: false },
  services: { title: t('reports.views.services.title'), question: t('reports.views.services.question'), range: true, compare: true },
  appointments: { title: t('reports.views.appointments.title'), question: t('reports.views.appointments.question'), range: true, compare: true },
  'peak-times': { title: t('reports.views.peakTimes.title'), question: t('reports.views.peakTimes.question'), range: true, compare: false },
  capacity: { title: t('reports.views.capacity.title'), question: t('reports.views.capacity.question'), range: true, compare: true },
  insurance: { title: t('reports.views.insurance.title'), question: t('reports.views.insurance.question'), range: true, compare: true },
  'inventory-value': { title: t('reports.views.inventoryValue.title'), question: t('reports.views.inventoryValue.question'), range: false, compare: false },
  consumption: { title: t('reports.views.consumption.title'), question: t('reports.views.consumption.question'), range: true, compare: true },
  'stock-movements': { title: t('reports.views.stockMovements.title'), question: t('reports.views.stockMovements.question'), range: true, compare: true },
  equipment: { title: t('reports.views.equipment.title'), question: t('reports.views.equipment.question'), range: true, compare: true },
});

// ── Filters ──────────────────────────────────────────────────────────────────
//
// One schema per report, read by BOTH the desktop filter row and the mobile sheet,
// so the two cannot offer different filters for the same report — which is exactly
// what happens when each surface builds its own controls.
//
//   control  'select' | 'segment' | 'search' | 'money' | 'date'
//   source   which option list the screen must load ('doctors', 'services', …)
//   primary  shown in the desktop row; the rest go behind «فیلترهای بیشتر»

const GROUP_BY = (key, options) => ({ key: 'group', control: 'segment', labelKey: 'reports.filters.groupBy', options, primary: true, urlKey: 'group', default: options[0] });

export const FILTER_SCHEMAS = {
  overview: [],
  pnl: [
    // `optionLabelKey`: these two are a BASIS, not a grouping — «تعهدی» and «نقدی»
    // live under `reports.basis.*`, which is also where the footnote reads them.
    { key: 'basis', control: 'segment', labelKey: 'reports.filters.basis', options: ['accrual', 'cash'], optionLabelKey: 'reports.basis', primary: true, default: 'accrual' },
  ],
  revenue: [
    GROUP_BY('group', ['day', 'doctor', 'service', 'kind', 'insurer']),
    { key: 'doctor', control: 'select', source: 'doctors', labelKey: 'reports.filters.doctor', primary: true },
    { key: 'kind', control: 'select', source: 'itemTypes', labelKey: 'reports.filters.itemType' },
    { key: 'insurer', control: 'select', source: 'insurers', labelKey: 'reports.filters.insurer' },
  ],
  collections: [
    GROUP_BY('group', ['day', 'method', 'cashier', 'doctor']),
    { key: 'method', control: 'select', source: 'methods', labelKey: 'reports.filters.method', primary: true },
    { key: 'cashier', control: 'select', source: 'cashiers', labelKey: 'reports.filters.cashier' },
    { key: 'doctor', control: 'select', source: 'doctors', labelKey: 'reports.filters.doctor' },
  ],
  expenses: [
    GROUP_BY('group', ['category', 'source', 'costCenter', 'payee', 'day']),
    { key: 'category', control: 'select', source: 'expenseCategories', labelKey: 'reports.filters.category', primary: true },
    { key: 'cc', control: 'select', source: 'costCentres', labelKey: 'reports.filters.costCenter', primary: true },
    { key: 'source', control: 'select', source: 'expenseSources', labelKey: 'reports.filters.source' },
    { key: 'approval', control: 'select', source: 'approvals', labelKey: 'reports.filters.approval' },
  ],
  receivables: [
    { key: 'bucket', control: 'segment', source: 'ageBuckets', labelKey: 'reports.filters.bucket', primary: true },
    { key: 'q', control: 'search', labelKey: 'reports.filters.search', primary: true },
    { key: 'doctor', control: 'select', source: 'doctors', labelKey: 'reports.filters.doctor' },
    { key: 'min', control: 'money', labelKey: 'reports.filters.minAmount' },
    { key: 'asOf', control: 'date', labelKey: 'reports.range.asOfDate' },
  ],
  shifts: [
    { key: 'cashier', control: 'select', source: 'cashiers', labelKey: 'reports.filters.cashier', primary: true },
    { key: 'status', control: 'select', source: 'shiftStatuses', labelKey: 'reports.filters.status', primary: true },
  ],
  bills: [],
  practitioners: [
    { key: 'profession', control: 'select', source: 'professions', labelKey: 'reports.filters.profession', primary: true },
    { key: 'doctor', control: 'select', source: 'doctors', labelKey: 'reports.filters.doctor', primary: true },
    { key: 'service', control: 'select', source: 'services', labelKey: 'reports.filters.service' },
  ],
  'practitioner-finance': [
    { key: 'doctor', control: 'select', source: 'doctors', labelKey: 'reports.filters.doctor', primary: true },
    { key: 'status', control: 'select', source: 'earningStatuses', labelKey: 'reports.filters.status', primary: true },
  ],
  patients: [
    { key: 'doctor', control: 'select', source: 'doctors', labelKey: 'reports.filters.doctor', primary: true },
    { key: 'via', control: 'select', source: 'registeredVia', labelKey: 'reports.filters.registeredVia' },
  ],
  'patient-value': [
    { key: 'q', control: 'search', labelKey: 'reports.filters.search', primary: true },
    { key: 'doctor', control: 'select', source: 'doctors', labelKey: 'reports.filters.doctor', primary: true },
    { key: 'min', control: 'money', labelKey: 'reports.filters.minAmount' },
  ],
  followups: [
    { key: 'status', control: 'segment', source: 'followUpStatuses', labelKey: 'reports.filters.status', primary: true },
    { key: 'assignee', control: 'select', source: 'staff', labelKey: 'reports.filters.assignee', primary: true },
    { key: 'kind', control: 'select', source: 'followUpKinds', labelKey: 'reports.filters.kind' },
  ],
  services: [
    { key: 'sort', control: 'segment', source: 'serviceSorts', labelKey: 'reports.filters.sortBy', primary: true },
    { key: 'serviceCat', control: 'select', source: 'serviceCategories', labelKey: 'reports.filters.serviceCategory', primary: true },
    { key: 'doctor', control: 'select', source: 'doctors', labelKey: 'reports.filters.doctor' },
    { key: 'insurer', control: 'select', source: 'insurers', labelKey: 'reports.filters.insurer' },
  ],
  appointments: [
    GROUP_BY('group', ['day', 'provider', 'status', 'type']),
    { key: 'doctor', control: 'select', source: 'providers', labelKey: 'reports.filters.provider', primary: true },
    { key: 'mode', control: 'select', source: 'schedulingModes', labelKey: 'reports.filters.mode' },
    { key: 'type', control: 'select', source: 'appointmentTypes', labelKey: 'reports.filters.type' },
  ],
  'peak-times': [
    { key: 'metric', control: 'segment', source: 'peakMetrics', labelKey: 'reports.filters.metric', primary: true },
    { key: 'doctor', control: 'select', source: 'providers', labelKey: 'reports.filters.provider', primary: true },
  ],
  capacity: [
    { key: 'doctor', control: 'select', source: 'doctors', labelKey: 'reports.filters.doctor', primary: true },
  ],
  insurance: [
    GROUP_BY('group', ['insurer', 'doctor', 'service']),
    { key: 'insurer', control: 'select', source: 'insurers', labelKey: 'reports.filters.insurer', primary: true },
    { key: 'kind', control: 'select', source: 'insurerKinds', labelKey: 'reports.filters.insurerKind' },
    { key: 'claimStatus', control: 'select', source: 'claimStatuses', labelKey: 'reports.filters.claimStatus' },
    { key: 'doctor', control: 'select', source: 'doctors', labelKey: 'reports.filters.doctor' },
  ],
  'inventory-value': [
    { key: 'tab', control: 'segment', source: 'inventoryTabs', labelKey: 'reports.filters.view', primary: true },
    { key: 'itemCat', control: 'select', source: 'itemCategories', labelKey: 'reports.filters.itemCategory' },
  ],
  consumption: [
    GROUP_BY('group', ['item', 'service', 'performer', 'day']),
    { key: 'itemCat', control: 'select', source: 'itemCategories', labelKey: 'reports.filters.itemCategory', primary: true },
    { key: 'item', control: 'select', source: 'items', labelKey: 'reports.filters.item' },
  ],
  'stock-movements': [
    { key: 'mtype', control: 'select', source: 'movementTypes', labelKey: 'reports.filters.movementType', primary: true },
    { key: 'item', control: 'select', source: 'items', labelKey: 'reports.filters.item', primary: true },
  ],
  equipment: [
    { key: 'status', control: 'select', source: 'equipmentStatuses', labelKey: 'reports.filters.status', primary: true },
    { key: 'criticality', control: 'select', source: 'criticalities', labelKey: 'reports.filters.criticality' },
  ],
};

/** Option vocabularies that are fixed rather than loaded. */
export const STATIC_OPTIONS = (t) => ({
  // Cost centres are a PLATFORM vocabulary, not a per-clinic list, so they belong
  // with the static options rather than in the lazy filter fetch: there is nothing
  // to load, and nothing that can be «not yet defined»
  // (docs/CLINIC_COST_CENTER_AUTO_MAPPING_DESIGN.md).
  costCentres: costCenterOptions(t).map(({ value, label }) => ({ id: value, label })),
  itemTypes: [
    { id: 'visit', label: t('reports.itemTypes.visit') },
    { id: 'medical_service', label: t('reports.itemTypes.medicalService') },
    { id: 'product', label: t('reports.itemTypes.product') },
    // «هزینهٔ لغو/عدم حضور» — the no-show fee's FINAL invoice line (PM-0657 C5).
    { id: 'fee', label: t('reports.itemTypes.fee') },
  ],
  methods: [
    { id: 'cash', label: t('reports.methods.cash') },
    { id: 'card', label: t('reports.methods.card') },
    { id: 'online', label: t('reports.methods.online') },
    { id: 'bank_transfer', label: t('reports.methods.bankTransfer') },
    { id: 'third_party', label: t('reports.methods.thirdParty') },
    { id: 'credit', label: t('reports.methods.credit') },
  ],
  // Mirrors EXPENSE_CATEGORIES in the accounting service's app/domain/expenses.py.
  expenseCategories: [
    { id: 'personnel', label: t('operations.expenseCategories.payroll') },
    { id: 'operating', label: t('operations.expenseCategories.operating') },
    { id: 'equipment', label: t('operations.expenseCategories.equipment') },
    { id: 'consumable', label: t('operations.expenseCategories.consumables') },
    { id: 'depreciation', label: t('operations.expenseCategories.depreciation') },
    { id: 'marketing', label: t('operations.expenseCategories.marketing') },
    { id: 'other', label: t('common.state.other') },
  ],
  // Mirrors EXPENSE_SOURCES in the same module. This is where «قبوض», «تعمیرات» and
  // «سرویس دوره‌ای» actually live — they are SOURCES of an expense, not categories,
  // which is why the P&L nests them one level under the category.
  expenseSources: [
    { id: 'manual', label: t('reports.expenseSources.manual') },
    { id: 'recurring', label: t('reports.expenseSources.recurring') },
    { id: 'bill_payment', label: t('reports.expenseSources.billPayment') },
    { id: 'inventory_purchase', label: t('reports.expenseSources.inventoryPurchase') },
    { id: 'inventory_withdrawal', label: t('reports.expenseSources.inventoryWithdrawal') },
    { id: 'equipment_purchase', label: t('reports.expenseSources.equipmentPurchase') },
    { id: 'equipment_service', label: t('reports.expenseSources.equipmentService') },
    { id: 'equipment_repair', label: t('reports.expenseSources.equipmentRepair') },
    { id: 'hr_payroll', label: t('reports.expenseSources.payroll') },
    { id: 'depreciation_accrual', label: t('reports.expenseSources.depreciation') },
  ],
  approvals: [
    { id: 'approved', label: t('reports.approvals.approved') },
    { id: 'pending', label: t('reports.approvals.pending') },
  ],
  ageBuckets: [
    { id: '', label: t('common.filters.all') },
    { id: '0-30', label: t('reports.ageBuckets.b0') },
    { id: '31-60', label: t('reports.ageBuckets.b31') },
    { id: '61-90', label: t('reports.ageBuckets.b61') },
    { id: '90+', label: t('reports.ageBuckets.b90') },
  ],
  shiftStatuses: [
    { id: 'open', label: t('reports.shiftStatuses.open') },
    { id: 'closed', label: t('reports.shiftStatuses.closed') },
  ],
  earningStatuses: [
    { id: 'accrued', label: t('reports.earningStatuses.accrued') },
    { id: 'realized', label: t('reports.earningStatuses.realized') },
    { id: 'settled', label: t('reports.earningStatuses.settled') },
  ],
  followUpStatuses: [
    { id: '', label: t('common.filters.all') },
    { id: 'open', label: t('reports.followUpStatuses.open') },
    { id: 'done', label: t('reports.followUpStatuses.done') },
  ],
  serviceSorts: [
    { id: 'count', label: t('reports.serviceSorts.popular') },
    { id: 'net', label: t('reports.serviceSorts.topRevenue') },
    { id: 'worst', label: t('reports.serviceSorts.lowPerforming') },
    { id: 'margin', label: t('reports.serviceSorts.topMargin') },
  ],
  schedulingModes: [
    { id: 'timed', label: t('reports.schedulingModes.timed') },
    { id: 'queue', label: t('reports.schedulingModes.queue') },
  ],
  appointmentTypes: [
    { id: 'in_person', label: t('reports.appointmentTypes.inPerson') },
    { id: 'online', label: t('reports.appointmentTypes.online') },
    { id: 'medical_service', label: t('reports.itemTypes.medicalService') },
  ],
  peakMetrics: [
    { id: 'booked', label: t('reports.peakMetrics.booked') },
    { id: 'completed', label: t('reports.peakMetrics.completed') },
    { id: 'arrived', label: t('reports.peakMetrics.arrived') },
  ],
  insurerKinds: [
    { id: 'basic', label: t('reports.insurerKinds.basic') },
    { id: 'supplementary', label: t('reports.insurerKinds.supplementary') },
  ],
  claimStatuses: [
    // The words the claims screen already uses (`insurance.claims.status.*`). These asked
    // for `insurance.claimStatus.*`, which no catalogue has, so the insurance report's
    // status filter listed «Partially Paid», «Written Off»… (PM-0729).
    { id: 'draft', label: t('insurance.claims.status.DRAFT') },
    { id: 'submitted', label: t('insurance.claims.status.SUBMITTED') },
    { id: 'accepted', label: t('insurance.claims.status.ACCEPTED') },
    { id: 'paid', label: t('insurance.claims.status.PAID') },
    { id: 'partially_paid', label: t('insurance.claims.status.PARTIALLY_PAID') },
    { id: 'rejected', label: t('insurance.claims.status.REJECTED') },
    { id: 'written_off', label: t('insurance.claims.status.WRITTEN_OFF') },
  ],
  inventoryTabs: [
    { id: 'value', label: t('reports.inventoryTabs.value') },
    { id: 'low', label: t('reports.inventoryTabs.low') },
    { id: 'expiry', label: t('reports.inventoryTabs.expiry') },
  ],
  movementTypes: [
    { id: 'RECEIVE', label: t('reports.movementTypes.receive') },
    { id: 'CONSUMPTION', label: t('reports.movementTypes.consumption') },
    { id: 'SALE', label: t('reports.movementTypes.sale') },
    { id: 'DISPOSAL', label: t('reports.movementTypes.disposal') },
    { id: 'ADJUSTMENT', label: t('reports.movementTypes.adjustment') },
  ],
  // All nine steps of the equipment status machine, in the Goods screens' words. The list had
  // three, so a machine that was «ordered», «idle», «sold» or «scrapped» could not be filtered to.
  equipmentStatuses: [
    { id: 'ordered', label: t('operations.equipment.status.ordered') },
    { id: 'received', label: t('operations.equipment.status.received') },
    { id: 'installed', label: t('operations.equipment.status.installed') },
    { id: 'in_service', label: t('operations.equipment.status.inService') },
    { id: 'idle', label: t('operations.equipment.status.idle') },
    { id: 'under_repair', label: t('operations.equipment.status.underRepair') },
    { id: 'retired', label: t('operations.equipment.status.retired') },
    { id: 'sold', label: t('operations.equipment.status.sold') },
    { id: 'scrapped', label: t('operations.equipment.status.scrapped') },
  ],
  criticalities: [
    { id: 'high', label: t('reports.criticalities.high') },
    { id: 'medium', label: t('reports.criticalities.medium') },
    { id: 'low', label: t('reports.criticalities.low') },
  ],
  professions: [
    { id: 'physician', label: t('reports.professions.physician') },
    { id: 'dentist', label: t('reports.professions.dentist') },
    { id: 'physiotherapist', label: t('reports.professions.physiotherapist') },
    { id: 'psychologist', label: t('reports.professions.psychologist') },
  ],
  registeredVia: [
    { id: 'QR_INVITATION', label: t('reports.registeredVia.qr') },
    { id: 'RECEPTION', label: t('reports.registeredVia.reception') },
  ],
  followUpKinds: [
    { id: 'call', label: t('reports.followUpKinds.call') },
    { id: 'visit', label: t('reports.followUpKinds.visit') },
    { id: 'other', label: t('common.state.other') },
  ],
});

// ── URL codec ────────────────────────────────────────────────────────────────
//
// The URL is the state (design §2 rule 7). A bookmarked report reopens identically,
// and the browser's back button steps through view changes rather than through every
// keystroke in a filter — which is why the surfaces PUSH a view change and REPLACE
// everything else.

const RESERVED = new Set(['view', 'range', 'from', 'to', 'cmp', 'sort', 'dir', 'page']);

export function parseParams(search, can) {
  const p = new URLSearchParams(search);
  const view = resolveView(p.get('view'), can);
  const meta = VIEW_META((k) => k)[view] || {};
  const preset = PRESET_IDS.includes(p.get('range')) ? p.get('range') : (meta.defaultRange || 'month');

  const filters = {};
  for (const spec of FILTER_SCHEMAS[view] || []) {
    const urlKey = spec.urlKey || spec.key;
    const raw = p.get(urlKey);
    if (raw !== null && raw !== '') filters[spec.key] = raw;
    else if (spec.default !== undefined) filters[spec.key] = spec.default;
  }

  return {
    view,
    preset,
    from: p.get('from') || null,
    to: p.get('to') || null,
    // Comparison defaults ON for reports that support it: the number a clinic acts
    // on is «۱۲٪ بیشتر از ماه قبل», not «۴۸۰ میلیون» on its own.
    compare: meta.compare !== false && p.get('cmp') !== '0',
    sort: p.get('sort') || null,
    dir: p.get('dir') === 'asc' ? 'asc' : 'desc',
    page: Math.max(1, Number(p.get('page')) || 1),
    filters,
  };
}

export function serializeParams(state) {
  const p = new URLSearchParams();
  if (state.view && state.view !== OVERVIEW) p.set('view', state.view);
  if (state.preset && state.preset !== 'month') p.set('range', state.preset);
  if (state.preset === 'custom') {
    if (state.from) p.set('from', state.from);
    if (state.to) p.set('to', state.to);
  }
  if (state.compare === false) p.set('cmp', '0');
  for (const [k, v] of Object.entries(state.filters || {})) {
    if (v !== null && v !== undefined && v !== '' && !RESERVED.has(k)) p.set(k, String(v));
  }
  if (state.sort) { p.set('sort', state.sort); p.set('dir', state.dir || 'desc'); }
  if (state.page && state.page > 1) p.set('page', String(state.page));
  return p.toString();
}

/** How many filters this person has actually set — the mobile badge's number. */
export function activeFilterCount(view, filters) {
  const schema = FILTER_SCHEMAS[view] || [];
  return schema.filter((s) => {
    const value = filters?.[s.key];
    if (value === undefined || value === null || value === '') return false;
    return value !== s.default;
  }).length;
}

// ── Insights ─────────────────────────────────────────────────────────────────
//
// Computed sentences, not commentary. Each rule has a THRESHOLD, fires only when its
// inputs exist, and says something the reader can act on. At most three per report,
// in the order listed, because a wall of observations is read as decoration and
// stops being read at all.
//
// The thresholds are constants here rather than inline so they can be argued with in
// one place — and so a future reviewer can see that «حاشیهٔ سود خوب» means 20%, not
// whatever the writer felt that day.

export const INSIGHT_THRESHOLDS = {
  goodMargin: 20,
  expenseSurge: 15,
  revenueFlat: 5,
  categoryDominates: 50,
  categoryRise: 25,
  lowCollection: 70,
  oldDebtShare: 30,
  topDoctorShare: 60,
  highCancelRate: 20,
  unusedServices: 3,
  longWait: 30,
  lowUtilization: 50,
  highShortfall: 10,
};

const T = INSIGHT_THRESHOLDS;
const up = (d) => (d?.pct ?? null);

/**
 * @param {string} view
 * @param {object} data
 * @param {{ row?: (row: object) => string }} [names]  how the OPEN view names one of its rows. A row's
 *   key is a category, a source, a cost centre or a day depending on the grouping — only the view
 *   knows which, so an insight that names a row asks it rather than quoting the key (PM-0740).
 * @returns [{ key, tone, values }] — the screen translates; this decides WHAT is worth saying.
 */
export function computeInsights(view, data, names = {}) {
  if (!data) return [];
  const out = [];
  const push = (key, tone, values) => out.push({ key, tone, values });

  if (view === 'pnl' || view === 'overview') {
    const f = view === 'overview' ? data.finance : data;
    if (!f) return [];
    const margin = f.marginPct;
    const net = f.netResult?.current ?? 0;
    if (net < 0) {
      push('P2', 'warning', { amount: Math.abs(net) });
    } else if (f.hasMargin && margin >= T.goodMargin) {
      push('P1', 'info', { margin });
    }
    const expenseDelta = up(f.expenses);
    const revenueDelta = up(f.netRevenue);
    if (expenseDelta !== null && revenueDelta !== null
      && expenseDelta >= T.expenseSurge && revenueDelta < T.revenueFlat) {
      push('P3', 'warning', { delta: expenseDelta });
    }
  }

  if (view === 'pnl') {
    const expenses = data.expenses?.current || 0;
    const top = (data.lines || [])
      .filter((l) => l.kind === 'expense' && l.level === 1)
      .sort((a, b) => b.current - a.current)[0];
    if (top && expenses && (top.current / expenses) * 100 >= T.categoryDominates) {
      push('P4', 'info', { category: top.key });
    }
  }

  if (view === 'revenue') {
    const d = up(data.netRevenue);
    if (d !== null && Math.round(d) !== 0) push('R1', 'info', { delta: d, rising: d > 0 });
    const share = data.netRevenue?.current
      ? (data.insuranceShare.current / data.netRevenue.current) * 100 : 0;
    if (share >= 40) push('R2', 'info', { share });
  }

  if (view === 'collections' && data.collectionRatio?.current != null) {
    if (data.collectionRatio.current < T.lowCollection) {
      push('C1', 'warning', { ratio: data.collectionRatio.current });
    }
  }

  if (view === 'expenses') {
    const top = (data.rows || [])[0];
    if (top?.deltaPct != null && top.deltaPct >= T.categoryRise) {
      push('E1', 'warning', { category: names.row ? names.row(top) : (top.label || top.key), delta: top.deltaPct });
    }
  }

  if (view === 'receivables' && data.total) {
    const share = (data.overNinety / data.total) * 100;
    if (share >= T.oldDebtShare) push('A1', 'warning', { share });
  }

  if (view === 'practitioners') {
    const rows = data.rows || [];
    const total = rows.reduce((s, r) => s + (r.net || 0), 0);
    const top = rows[0];
    if (total && top?.net && (top.net / total) * 100 >= T.topDoctorShare) {
      push('D1', 'info', { name: top.name, share: (top.net / total) * 100 });
    }
    const risky = rows.find((r) => r.cancelRate >= T.highCancelRate);
    if (risky) push('D2', 'warning', { name: risky.name, rate: risky.cancelRate });
  }

  if (view === 'services' && data.unusedCount >= T.unusedServices) {
    push('S1', 'info', { count: data.unusedCount });
  }

  if (view === 'appointments' && (data.avgWait?.current ?? 0) >= T.longWait) {
    push('V1', 'warning', { minutes: data.avgWait.current });
  }

  if (view === 'capacity' && (data.utilizationPct?.current ?? 100) < T.lowUtilization) {
    push('K1', 'warning', { pct: data.utilizationPct.current });
  }

  if (view === 'insurance' && data.claimed?.current) {
    const share = (data.shortfall.current / data.claimed.current) * 100;
    if (share >= T.highShortfall) push('I1', 'warning', { share });
  }

  if (view === 'inventory-value' && data.lowStockCount > 0) {
    push('N1', 'warning', { count: data.lowStockCount });
  }

  return out.slice(0, 3);
}

// ── Empty-state iconography ──────────────────────────────────────────────────
//
// One glyph per DOMAIN, naming that domain's absence rather than emptiness in
// general (packages/DESIGN.md §Empty-state iconography). The lucide component itself
// is chosen by the screen; this maps the view to a name so both surfaces pick the
// same one.
export const EMPTY_ICON = {
  finance: 'ReceiptText',
  practitioners: 'Stethoscope',
  patients: 'Users',
  services: 'ClipboardList',
  appointments: 'CalendarDays',
  insurance: 'ShieldCheck',
  inventory: 'Package',
  equipment: 'Wrench',
};

export function groupOfView(view) {
  const group = RAIL_GROUPS((k) => k).find((g) => g.items.some((i) => i.key === view));
  return group?.key || 'finance';
}
